import { useState } from 'react';
import { EmptyState } from '../EmptyState';
import { ConfirmDialog } from '../Modal';
import { CapMeter } from './CapMeter';
import { AddBossesModal } from './AddBossesModal';
import { CharacterBossRow } from './CharacterBossRow';
import { CollapsibleSection } from '../goals/CollapsibleSection';
import { formatCrystalValue, groupBossesByTier, type BossTier } from '../../lib/bossGrouping';
import type { BossDto } from '../../lib/reference';
import type { BossMatrixCharacter, IncomeResponse } from '../../lib/bosses';

interface CharacterBossListProps {
  character: BossMatrixCharacter;
  bossCatalog: BossDto[];
  income: IncomeResponse | undefined;
  editableForTier: (tier: BossTier) => boolean;
  periodKeyForTier: (tier: BossTier) => string;
  planPendingKey: string | null;
  clearPendingKey: string | null;
  deletePendingKey: string | null;
  /** `5` on a Heroic account, `1` otherwise — see `BossCellProps.crystalMultiplier`. */
  crystalMultiplier: number;
  onSetDifficulty: (character: BossMatrixCharacter, boss: BossDto, difficulty: string) => void;
  onSetPartySize: (character: BossMatrixCharacter, boss: BossDto, partySize: number) => void;
  /** Row-half clicks and the "Cleared" badge (REV2 §1/§2) — always upserts a clear with an explicit `crystalSold`. */
  onSetClearedWithGold: (
    character: BossMatrixCharacter,
    boss: BossDto,
    periodKey: string,
    cleared: boolean,
    crystalSold: boolean,
  ) => void;
  onSetCrystalSold: (character: BossMatrixCharacter, boss: BossDto, periodKey: string, crystalSold: boolean) => void;
  /** REV5: commits the Gollux coin input — always the current period's clear, so no `periodKey` arg. */
  onSetCoinsEarned: (character: BossMatrixCharacter, boss: BossDto, coinsEarned: number) => void;
  onDeletePlan: (character: BossMatrixCharacter, boss: BossDto) => void;
  /** Per-tier "Complete all" (REV2 §5) — resolves once every reachable per-boss upsert has settled. */
  onCompleteAllTier: (character: BossMatrixCharacter, tier: BossTier) => Promise<void>;
  /** True while a Complete-all run for this character+tier is in flight — keys as `${characterId}:${tier}`. */
  completeAllPendingKey: string | null;
}

/**
 * One character's boss-tab content (the redesign's core surface): only bosses this character has a
 * plan for, grouped WEEKLY/MONTHLY/DAILY and unlock-sorted the same way the old whole-account matrix
 * was, each tier a `CollapsibleSection` with a "Complete all" action in its header and each row a
 * `CharacterBossRow` (REV2 — docs/specs/REV2-bosses-ux.md: split-click row, party-size and
 * cleared/gold-claimed badges) plus a remove action that deletes the plan (`DELETE
 * /api/boss-plan/**`) — clears history stays, only the plan goes. A character's boss list *is* its
 * `boss_plan` rows; there is no separate "visibility" concept anymore (see `POST-MVP-fixes.md` §8
 * for the hidden-boss deprecation this replaces).
 */
export function CharacterBossList({
  character,
  bossCatalog,
  income,
  editableForTier,
  periodKeyForTier,
  planPendingKey,
  clearPendingKey,
  deletePendingKey,
  crystalMultiplier,
  onSetDifficulty,
  onSetPartySize,
  onSetClearedWithGold,
  onSetCrystalSold,
  onSetCoinsEarned,
  onDeletePlan,
  onCompleteAllTier,
  completeAllPendingKey,
}: CharacterBossListProps) {
  const [addOpen, setAddOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<BossDto | null>(null);

  const plannedSlugs = new Set(character.plans.map((p) => p.bossSlug));
  const groups = groupBossesByTier(bossCatalog.filter((b) => plannedSlugs.has(b.slug)));
  const stats = income?.characters.find((c) => c.characterId === character.characterId);
  const hasBosses = character.plans.length > 0;
  // REV5: `character.clears` is already scoped to the visible week (BossService.queryClears), so
  // summing every Gollux clear in it — not just today's — is exactly "this week's" coin total.
  const weeklyGolluxCoins = character.clears
    .filter((c) => c.bossSlug === 'gollux')
    .reduce((sum, c) => sum + (c.coinsEarned ?? 0), 0);

  return (
    <div
      id={`boss-tabpanel-${character.characterId}`}
      role="tabpanel"
      aria-labelledby={`boss-tab-${character.characterId}`}
      className="space-y-4"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-text-faint">
          {character.name}&rsquo;s bosses
        </h2>
        {/* Only shown once there's a list to add to — an empty list already offers "Add bosses" as
            its own EmptyState action, and showing both here would duplicate that accessible name. */}
        {hasBosses && (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="min-h-tap rounded-lg bg-accent px-4 text-sm font-semibold text-accent-contrast shadow-sm transition-colors hover:bg-accent-strong"
          >
            Add bosses
          </button>
        )}
      </div>

      {!hasBosses ? (
        <EmptyState
          title={`No bosses on ${character.name}'s list yet`}
          note="Add the ones they clear each week — you can add or remove any time."
          actionLabel="Add bosses"
          actionOnClick={() => setAddOpen(true)}
        />
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const periodKey = periodKeyForTier(group.tier);
            const editable = editableForTier(group.tier);
            const clearedCount = group.bosses.filter((boss) =>
              character.clears.some((c) => c.bossSlug === boss.slug && c.periodKey === periodKey),
            ).length;
            // "Complete all" targets planned + not-yet-cleared + not-hidden bosses only (REV2 §5) —
            // hiddenBossSlugs is otherwise unused by this per-character list (its own doc comment:
            // a character's list *is* its boss_plan rows), but Complete-all still honors it so a
            // boss the player hid from their own view is never force-cleared behind their back.
            const remaining = group.bosses.filter(
              (boss) =>
                !character.hiddenBossSlugs.includes(boss.slug) &&
                !character.clears.some((c) => c.bossSlug === boss.slug && c.periodKey === periodKey),
            ).length;
            const completeAllKey = `${character.characterId}:${group.tier}`;
            const completeAllPending = completeAllPendingKey === completeAllKey;
            const completeAllDisabled = !editable || remaining === 0 || completeAllPending;
            return (
              <CollapsibleSection
                key={group.tier}
                title={group.label}
                subtitle={`${clearedCount}/${group.bosses.length} cleared`}
                headerAction={
                  <button
                    type="button"
                    disabled={completeAllDisabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      void onCompleteAllTier(character, group.tier);
                    }}
                    aria-label={`Complete all ${group.label.toLowerCase()} bosses for ${character.name}`}
                    className="min-h-tap rounded-lg border border-border-strong bg-bg-elevated-hover px-3 text-xs font-semibold text-text transition-colors hover:bg-bg-elevated disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {completeAllPending ? 'Completing…' : 'Complete all'}
                  </button>
                }
              >
                <ul className="space-y-2">
                  {group.bosses.map((boss) => {
                    const plan = character.plans.find((p) => p.bossSlug === boss.slug);
                    const clear = character.clears.find(
                      (c) => c.bossSlug === boss.slug && c.periodKey === periodKey,
                    );
                    const cellKey = `${character.characterId}:${boss.slug}`;
                    return (
                      <CharacterBossRow
                        key={boss.slug}
                        boss={boss}
                        character={character}
                        characterName={character.name}
                        plan={plan}
                        clear={clear}
                        editable={editable}
                        planPending={planPendingKey === cellKey}
                        clearPending={clearPendingKey === cellKey}
                        deletePending={deletePendingKey === cellKey}
                        crystalMultiplier={crystalMultiplier}
                        onSetDifficulty={(difficulty) => onSetDifficulty(character, boss, difficulty)}
                        onSetPartySize={(partySize) => onSetPartySize(character, boss, partySize)}
                        onSetClearedWithGold={(cleared, crystalSold) =>
                          onSetClearedWithGold(character, boss, periodKey, cleared, crystalSold)
                        }
                        onSetCrystalSold={(sold) => onSetCrystalSold(character, boss, periodKey, sold)}
                        weeklyCoinsTotal={boss.slug === 'gollux' ? weeklyGolluxCoins : undefined}
                        onSetCoinsEarned={
                          boss.slug === 'gollux'
                            ? (coinsEarned) => onSetCoinsEarned(character, boss, coinsEarned)
                            : undefined
                        }
                        onRemove={() => setConfirmRemove(boss)}
                      />
                    );
                  })}
                </ul>
              </CollapsibleSection>
            );
          })}
        </div>
      )}

      {hasBosses && (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-bg p-3">
          <CapMeter
            label="Crystals"
            current={stats?.weeklyCrystalsSold ?? 0}
            cap={stats?.characterCap ?? 14}
            reached={stats?.characterCapReached ?? false}
          />
          <div className="text-right">
            <p className="font-display text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-text-faint">
              Weekly income
            </p>
            <p className="text-lg font-semibold tabular-nums text-crystal">
              {formatCrystalValue(stats?.totalMesos ?? 0)}
            </p>
          </div>
        </div>
      )}

      {addOpen && (
        <AddBossesModal
          character={character}
          bossCatalog={bossCatalog}
          crystalMultiplier={crystalMultiplier}
          planPendingKey={planPendingKey}
          onSetDifficulty={(boss, difficulty) => onSetDifficulty(character, boss, difficulty)}
          onClose={() => setAddOpen(false)}
        />
      )}

      {confirmRemove && (
        <ConfirmDialog
          title="Remove boss"
          message={
            <>
              Remove <strong>{confirmRemove.name}</strong> from {character.name}&rsquo;s list? Clears
              already recorded for it are kept — this only removes it from the list.
            </>
          }
          confirmLabel="Remove"
          danger
          pending={deletePendingKey === `${character.characterId}:${confirmRemove.slug}`}
          onConfirm={() => {
            onDeletePlan(character, confirmRemove);
            setConfirmRemove(null);
          }}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </div>
  );
}
